import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, MapPin, ChevronDown } from 'lucide-react';
import { SITE, NAV, IMAGES } from '../data/site';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();

  const go = (path) => {
    setOpen(false);
    setExpanded(null);
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-brand-dark text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 text-xs sm:px-6 lg:px-12">
          <p className="flex items-center gap-2 font-medium text-blue-100" data-testid="header-address">
            <MapPin className="h-3.5 w-3.5 text-sky-300" />
            <span className="hidden sm:inline">{SITE.address}, {SITE.cityStateZip}</span>
            <span className="sm:hidden">Stanfield, NC</span>
          </p>
          <p className="hidden font-mono font-bold uppercase tracking-widest text-blue-200 sm:block" data-testid="header-years-badge">
            Over 60 Years of Local Service
          </p>
        </div>
      </div>

      <div className="border-b border-white/10 bg-brand/95 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between gap-4 py-3">
            <Link to="/" data-testid="header-logo-link" className="flex shrink-0 items-center gap-3">
              <img
                src={IMAGES.logoTransparent}
                alt="B.E. Holbrooks Co. logo - HVAC and plumbing company in Stanfield, NC"
                className="h-12 w-auto sm:h-14 xl:h-16"
              />
            </Link>

            <div className="flex items-center gap-2">
              <a
                href={SITE.phoneHref}
                data-testid="header-call-button"
                className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-white px-5 py-2.5 font-display text-sm font-bold text-brand shadow-md shadow-navy/20 transition-all hover:-translate-y-0.5 hover:bg-blue-50 md:flex"
              >
                <Phone className="h-4 w-4" />
                Call {SITE.phone}
              </a>
              <button
                onClick={() => setOpen(!open)}
                data-testid="mobile-menu-button"
                aria-label="Toggle navigation menu"
                className="rounded-md p-2 text-white transition-colors hover:bg-white/10 xl:hidden"
              >
                {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          <nav className="hidden items-center justify-center gap-1 border-t border-white/15 py-1.5 xl:flex" aria-label="Main navigation">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label} className="group relative">
                  <NavLink
                    to={item.path}
                    data-testid={item.testid}
                    className={({ isActive }) =>
                      `flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors hover:bg-white/10 hover:text-white ${
                        isActive ? 'bg-white/15 text-white' : 'text-white/85'
                      }`
                    }
                  >
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
                  </NavLink>
                  <div className="invisible absolute left-0 top-full z-50 w-64 translate-y-2 rounded-xl border border-slate-200 bg-white p-2 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    {item.children.map((child) => (
                      <button
                        key={child.path}
                        onClick={() => go(child.path)}
                        data-testid={`dropdown-${child.path.replace(/\//g, '') || 'root'}`}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-600 transition-colors hover:bg-brand-soft hover:text-brand"
                      >
                        {child.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.path}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors hover:bg-white/10 hover:text-white ${
                      isActive ? 'bg-white/15 text-white' : 'text-white/85'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>
        </div>
      </div>

      {open && (
        <div className="max-h-[75vh] overflow-y-auto border-b border-slate-200 bg-white shadow-2xl xl:hidden" data-testid="mobile-menu">
          <nav className="px-4 py-4" aria-label="Mobile navigation">
            {NAV.map((item) => (
              <div key={item.label} className="border-b border-slate-100 last:border-0">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => go(item.path)}
                    data-testid={`mobile-${item.testid}`}
                    className="flex-1 py-3 text-left font-display text-base font-bold text-navy"
                  >
                    {item.label}
                  </button>
                  {item.children && (
                    <button
                      onClick={() => setExpanded(expanded === item.label ? null : item.label)}
                      aria-label={`Expand ${item.label}`}
                      data-testid={`mobile-expand-${item.testid}`}
                      className="p-3 text-slate-500"
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform ${expanded === item.label ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                {item.children && expanded === item.label && (
                  <div className="pb-3 pl-4">
                    {item.children.map((child) => (
                      <button
                        key={child.path}
                        onClick={() => go(child.path)}
                        className="block w-full py-2 text-left text-sm font-medium text-slate-600 hover:text-brand"
                      >
                        {child.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a
              href={SITE.phoneHref}
              data-testid="mobile-menu-call-button"
              className="mt-4 flex items-center justify-center gap-2 rounded-full bg-brand py-3 font-display text-base font-bold text-white"
            >
              <Phone className="h-5 w-5" /> Call {SITE.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
